import React, {useCallback, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';

import PWAException from 'coreClasses/PWAException';
import {IReturnAuthorizationPayload} from 'coreClasses/returnAuthorization/IReturnOrderData';
import ReturnOrderDataModel from 'coreClasses/returnAuthorization/ReturnOrderDataModel';
import ViewportMgr from 'coreClasses/view/ViewportMgr';
import {useLocalesMgr, useNotify} from 'coreHooks';
import useReturnAuthorizationMgr from 'coreHooks/useReturnAuthorizationMgr';

import {IReturnAuthorizationCreateFormProps} from './IReturnAuthorizationCreateForm';
import messages from './messages';
import {ProductData, Step1, Step2, Step3} from './Subcomponents';

import styles from './ReturnAuthorizationCreateForm.module.scss';

const ReturnAuthorizationCreateForm = ({
	returnOrder,
	handleSuccess,
	countryCode,
}: IReturnAuthorizationCreateFormProps) => {
	const returnAuthorizationMgr = useReturnAuthorizationMgr();
	const [step, setStep] = useState(1);
	const [returnReasons, setReturnReasons] = useState([]);
	// TODO: Add exchange reason state
	const [returnOrderData, setReturnOrderData] = useState(new ReturnOrderDataModel(null));
	const notify = useNotify();
	const intl = useIntl();

	const localesMgr = useLocalesMgr();
	const currentLocale = localesMgr.getCurrentLocale();
	const currentLanguageCode = currentLocale.getLanguageCode();

	useEffect(() => {
		(async () => {
			let returnReasons = await returnAuthorizationMgr.getReturnReasons();
			returnReasons = returnReasons.sort((a, b) => a.label?.localeCompare(b.label));
			setReturnReasons(returnReasons);
		})();
	}, [currentLanguageCode]);

	useEffect(() => {
		ViewportMgr.scrollTo(0, 0, 'smooth');
	}, [step]);

	const handleStep = async (values: Record<string, any>) => {
		let gotoNext = true;
		const nextStep = step + 1;

		let refundMethod = values['refundMethod']
			? values['refundMethod']
			: returnOrder.getPaymentInstruments()[0].getPaymentMethodId() || null;

		if (step === 1) {
			let products = [];

			for (let key in values) {
				const [fieldName, productItemCode] = key.split('-');

				if (fieldName === 'returnProduct') {
					const lineItemID = productItemCode.split('_')[0];
					let returnReason: string = values[`returnReason-${productItemCode}`];
					let isExchange = false;
					let product = {lineItemID, returnReason, isExchange};
					products.push(product);
				}

				if (fieldName === 'exchangeProduct') {
					const lineItemID = productItemCode.split('_')[0];
					let newItemID:string = values[`exchangeItem-${productItemCode}`];
					let newColor:string = values[`exchangeColor-${productItemCode}`];
					let newSize:string = values[`exchangeSize-${productItemCode}`];
					let exchangeReason: string = values[`exchangeReason-${productItemCode}`];
					let isExchange = true;
					let product = {lineItemID, exchangeReason, isExchange, newItemID, newColor, newSize};
					products.push(product);
				}
			}

			let newReturnOrderData = new ReturnOrderDataModel(products);
			setReturnOrderData(newReturnOrderData);

			newReturnOrderData.setRefundMethod(refundMethod);

			//if cash on delivery, save payment info
			if (refundMethod === 'CASH_ON_DELIVERY') {
				newReturnOrderData.setBankAccountHolder(values.bankAccountHolder);
				newReturnOrderData.setBeneficiaryBank(values.beneficiaryBank);
				newReturnOrderData.setBICSWIFTCode(values.BICSWIFTCode);
				newReturnOrderData.setIBAN(values.IBAN);
				newReturnOrderData.setPhoneNumber(values.phoneNumber);
			}

			setReturnOrderData(newReturnOrderData);
		}

		if (nextStep === 3) {
			gotoNext = await createReturnAuthorization();
		}

		if (gotoNext) {
			setStep(nextStep);
		}
	};

	const goToStep = useCallback((step: number) => {
		setStep(step);

		if (step === 1) {
			setReturnOrderData(new ReturnOrderDataModel(null));
		}
	}, []);

	const createReturnAuthorization = async () => {
		const payload: IReturnAuthorizationPayload = {
			orderNo: returnOrder.getOrderNumber(),
			items: [],
		};

		// handle selected refund method
		if (returnOrderData.getRefundMethod() === 'STORE_CREDIT') {
			payload.storeCredit = true;
		}

		if (returnOrderData.getRefundMethod() === 'CASH_ON_DELIVERY') {
			const details = returnOrderData.getPaymentDetails();
			payload.bankAccountHolder = details.bankAccountHolder;
			payload.beneficiaryBank = details.beneficiaryBank;
			payload.BICSWIFTCode = details.BICSWIFTCode;
			payload.IBAN = details.IBAN;
			payload.phoneNumber = returnOrderData.getPhoneNumber();
		}

		let product = returnOrderData.getProducts();

		product.forEach((element) => {
			payload.items.push({
				productId: returnOrder.getProductItem(element.lineItemID).getProductId(),
				returnReasonCode: element.returnReason,
				exchangeReasonCode: element.exchangeReason,
				isExchange: element.isExchange,
				newProductId: element.newItemID
			});
		});

		try {
			await returnAuthorizationMgr.createReturnAuthorization(payload);
		} catch (e) {
			if (e instanceof PWAException) {
				const notifyMessage = messages[e.message] ? intl.formatMessage(messages[e.message]) : e.message;

				notify.error(notifyMessage);
			} else {
				notify.error(intl.formatMessage(messages.generalError));
			}

			return false;
		}

		return true;
	};

	const renderForm = () => {
		switch (step) {
			case 1:
				return (
					<Step1
						handleStep={handleStep}
						returnOrder={returnOrder}
						returnReasons={returnReasons}
						exchangeReasons={returnReasons}
						countryCode={countryCode}
						step={step}
					/>
				);
			case 2:
				return (
					<Step2
						handleStep={handleStep}
						setStep={setStep}
						returnOrder={returnOrder}
						returnOrderData={returnOrderData}
						returnReasons={returnReasons}
						exchangeReasons={returnReasons}
						goToStep={goToStep}
						step={step}
					/>
				);
			case 3:
				return <Step3 handleSuccess={handleSuccess} />;
			default:
				return <></>;
		}
	};

	return (
		<div className={styles['return-create__form']}>
			{renderForm()}
		</div>
	);
};

ReturnAuthorizationCreateForm.Step1 = Step1;
ReturnAuthorizationCreateForm.Step2 = Step2;
ReturnAuthorizationCreateForm.Step3 = Step3;
ReturnAuthorizationCreateForm.ProductData = ProductData;

export default ReturnAuthorizationCreateForm;
